export interface Point2D {
  x: number;
  y: number;
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const distance = (p1: Point2D, p2: Point2D) =>
  Math.hypot(p1.x - p2.x, p1.y - p2.y);

export const loadImageElementFromFile = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const loadImageElementFromUrl = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

export const orderQuadPoints = (points: Point2D[]): Point2D[] => {
  if (!Array.isArray(points) || points.length !== 4) {
    throw new Error('Quad points must contain exactly 4 points.');
  }

  const sortedByX = [...points].sort((a, b) => a.x - b.x);
  const left = sortedByX.slice(0, 2).sort((a, b) => a.y - b.y);
  const right = sortedByX.slice(2, 4).sort((a, b) => a.y - b.y);

  return [left[0], right[0], right[1], left[1]];
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type = 'image/png',
  quality = 0.92,
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob from canvas.'));
          return;
        }
        resolve(blob);
      },
      type,
      quality,
    );
  });

const solveLinearSystem = (A: number[][], b: number[]): number[] => {
  const n = A.length;
  const M = A.map((row, i) => [...row, b[i]]);

  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let row = col + 1; row < n; row += 1) {
      if (Math.abs(M[row][col]) > Math.abs(M[pivot][col])) {
        pivot = row;
      }
    }

    if (Math.abs(M[pivot][col]) < 1e-10) {
      throw new Error('Cannot solve homography matrix.');
    }

    if (pivot !== col) {
      [M[col], M[pivot]] = [M[pivot], M[col]];
    }

    const divisor = M[col][col];
    for (let k = col; k <= n; k += 1) {
      M[col][k] /= divisor;
    }

    for (let row = 0; row < n; row += 1) {
      if (row === col) continue;
      const factor = M[row][col];
      for (let k = col; k <= n; k += 1) {
        M[row][k] -= factor * M[col][k];
      }
    }
  }

  return M.map((row) => row[n]);
};

const computeHomography = (srcPts: Point2D[], dstPts: Point2D[]) => {
  const A: number[][] = [];
  const b: number[] = [];

  for (let i = 0; i < 4; i += 1) {
    const { x, y } = srcPts[i];
    const { x: u, y: v } = dstPts[i];

    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    b.push(v);
  }

  const h = solveLinearSystem(A, b);
  return [
    [h[0], h[1], h[2]],
    [h[3], h[4], h[5]],
    [h[6], h[7], 1],
  ];
};

const applyHomography = (H: number[][], x: number, y: number): Point2D => {
  const d = H[2][0] * x + H[2][1] * y + H[2][2];
  if (Math.abs(d) < 1e-10) return { x: 0, y: 0 };
  return {
    x: (H[0][0] * x + H[0][1] * y + H[0][2]) / d,
    y: (H[1][0] * x + H[1][1] * y + H[1][2]) / d,
  };
};

const getPixel = (
  data: Uint8ClampedArray,
  width: number,
  x: number,
  y: number,
  channel: number,
) => {
  const px = clamp(Math.floor(x), 0, width - 1);
  const py = clamp(Math.floor(y), 0, data.length / 4 / width - 1);
  return data[(py * width + px) * 4 + channel];
};

const bilinearSample = (
  data: Uint8ClampedArray,
  width: number,
  x: number,
  y: number,
) => {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = x0 + 1;
  const y1 = y0 + 1;
  const wx = x - x0;
  const wy = y - y0;

  return [0, 1, 2].map((c) => {
    const p00 = getPixel(data, width, x0, y0, c);
    const p10 = getPixel(data, width, x1, y0, c);
    const p01 = getPixel(data, width, x0, y1, c);
    const p11 = getPixel(data, width, x1, y1, c);
    const top = p00 * (1 - wx) + p10 * wx;
    const bottom = p01 * (1 - wx) + p11 * wx;
    return top * (1 - wy) + bottom * wy;
  });
};

const makeSourceCanvas = (image: HTMLImageElement, maxSourceEdge = 2400) => {
  const hasSourceLimit = Number.isFinite(maxSourceEdge) && maxSourceEdge > 0;
  const ratio = hasSourceLimit
    ? Math.min(maxSourceEdge / image.width, maxSourceEdge / image.height, 1)
    : 1;
  const width = Math.max(1, Math.round(image.width * ratio));
  const height = Math.max(1, Math.round(image.height * ratio));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')!.drawImage(image, 0, 0, width, height);

  return { canvas, ratio };
};

export type PreprocessMode = 'warp' | 'crop' | 'warp-crop';

export interface CreateOcrReadyImageOptions {
  image: HTMLImageElement;
  points: Point2D[];
  preprocessMode?: PreprocessMode;
  outputType?: string;
  outputQuality?: number;
  maxSourceEdge?: number;
  maxWarpWidth?: number;
  maxWarpHeight?: number;
}

export interface OcrReadyImageResult {
  blob: Blob;
  width: number;
  height: number;
  outputMode: string;
}

/** 레거시 `ocrImagePreprocess.js` — 4점 워핑/크롭으로 OCR용 이미지 생성 */
export const createOcrReadyImage = async ({
  image,
  points,
  preprocessMode = 'warp',
  outputType = 'image/png',
  outputQuality = 0.92,
  maxSourceEdge = 2400,
  maxWarpWidth = 2400,
  maxWarpHeight = 3200,
}: CreateOcrReadyImageOptions): Promise<OcrReadyImageResult> => {
  const { canvas: sourceCanvas, ratio } = makeSourceCanvas(image, maxSourceEdge);

  const scaledPoints = orderQuadPoints(points).map((p) => ({
    x: p.x * ratio,
    y: p.y * ratio,
  }));

  const [tl, tr, br, bl] = scaledPoints;

  if (preprocessMode === 'crop') {
    const minX = clamp(
      Math.floor(Math.min(tl.x, tr.x, br.x, bl.x)),
      0,
      sourceCanvas.width - 1,
    );
    const minY = clamp(
      Math.floor(Math.min(tl.y, tr.y, br.y, bl.y)),
      0,
      sourceCanvas.height - 1,
    );
    const maxX = clamp(
      Math.ceil(Math.max(tl.x, tr.x, br.x, bl.x)),
      minX + 1,
      sourceCanvas.width,
    );
    const maxY = clamp(
      Math.ceil(Math.max(tl.y, tr.y, br.y, bl.y)),
      minY + 1,
      sourceCanvas.height,
    );

    const width = Math.max(1, maxX - minX);
    const height = Math.max(1, maxY - minY);
    const widthLimit =
      Number.isFinite(maxWarpWidth) && maxWarpWidth > 0 ? maxWarpWidth : width;
    const heightLimit =
      Number.isFinite(maxWarpHeight) && maxWarpHeight > 0
        ? maxWarpHeight
        : height;
    const scale = Math.min(widthLimit / width, heightLimit / height, 1);
    const outWidth = Math.max(1, Math.round(width * scale));
    const outHeight = Math.max(1, Math.round(height * scale));

    const outCanvas = document.createElement('canvas');
    outCanvas.width = outWidth;
    outCanvas.height = outHeight;
    const outCtx = outCanvas.getContext('2d')!;
    outCtx.drawImage(
      sourceCanvas,
      minX,
      minY,
      width,
      height,
      0,
      0,
      outWidth,
      outHeight,
    );

    const blob = await canvasToBlob(outCanvas, outputType, outputQuality);
    return { blob, width: outWidth, height: outHeight, outputMode: 'crop' };
  }

  const width = Math.max(
    1,
    Math.round(Math.max(distance(tr, tl), distance(br, bl))),
  );
  const height = Math.max(
    1,
    Math.round(Math.max(distance(br, tr), distance(bl, tl))),
  );
  const widthLimit =
    Number.isFinite(maxWarpWidth) && maxWarpWidth > 0 ? maxWarpWidth : width;
  const heightLimit =
    Number.isFinite(maxWarpHeight) && maxWarpHeight > 0
      ? maxWarpHeight
      : height;
  const scale = Math.min(widthLimit / width, heightLimit / height, 1);

  const outWidth = Math.max(1, Math.round(width * scale));
  const outHeight = Math.max(1, Math.round(height * scale));

  const srcCtx = sourceCanvas.getContext('2d')!;
  const srcImageData = srcCtx.getImageData(
    0,
    0,
    sourceCanvas.width,
    sourceCanvas.height,
  );
  const srcRgba = srcImageData.data;

  const outCanvas = document.createElement('canvas');
  outCanvas.width = outWidth;
  outCanvas.height = outHeight;
  const outCtx = outCanvas.getContext('2d')!;
  const outImageData = outCtx.createImageData(outWidth, outHeight);

  const dstPts: Point2D[] = [
    { x: 0, y: 0 },
    { x: outWidth - 1, y: 0 },
    { x: outWidth - 1, y: outHeight - 1 },
    { x: 0, y: outHeight - 1 },
  ];
  const H = computeHomography(dstPts, scaledPoints);

  for (let y = 0; y < outHeight; y += 1) {
    for (let x = 0; x < outWidth; x += 1) {
      const srcPoint = applyHomography(H, x, y);
      const [r, g, b] = bilinearSample(
        srcRgba,
        sourceCanvas.width,
        srcPoint.x,
        srcPoint.y,
      );
      const i = (y * outWidth + x) * 4;
      outImageData.data[i] = r;
      outImageData.data[i + 1] = g;
      outImageData.data[i + 2] = b;
      outImageData.data[i + 3] = 255;
    }
  }
  outCtx.putImageData(outImageData, 0, 0);

  const blob = await canvasToBlob(outCanvas, outputType, outputQuality);
  return {
    blob,
    width: outWidth,
    height: outHeight,
    outputMode: preprocessMode === 'warp-crop' ? 'warp-crop' : 'warp',
  };
};
